import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TIME_OPTIONS = [
  '6:00 AM', '6:30 AM', '7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM',
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
  '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM'
];

const AvailabilitySection = ({ availability, pickupTime, dropoffTime, onChange, onPickupTimeChange, onDropoffTimeChange }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState(availability || []);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();
    return { daysInMonth, startingDayOfWeek };
  };

  const handleDateClick = (date) => {
    const dateString = date?.toISOString()?.split('T')?.[0];
    const newSelectedDates = selectedDates?.includes(dateString)
      ? selectedDates?.filter(d => d !== dateString)
      : [...selectedDates, dateString];
    setSelectedDates(newSelectedDates);
    onChange(newSelectedDates);
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth?.getFullYear(), currentMonth?.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth?.getFullYear(), currentMonth?.getMonth() + 1));
  };

  const handleSelectAll = () => {
    const { daysInMonth } = getDaysInMonth(currentMonth);
    const allDates = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth?.getFullYear(), currentMonth?.getMonth(), day);
      if (date >= new Date(new Date()?.setHours(0, 0, 0, 0))) {
        allDates?.push(date?.toISOString()?.split('T')?.[0]);
      }
    }
    const newSelectedDates = [...new Set([...selectedDates, ...allDates])];
    setSelectedDates(newSelectedDates);
    onChange(newSelectedDates);
  };

  const handleClearAll = () => {
    setSelectedDates([]);
    onChange([]);
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const days = [];
  for (let i = 0; i < startingDayOfWeek; i++) { days?.push(null); }
  for (let day = 1; day <= daysInMonth; day++) {
    days?.push(new Date(currentMonth?.getFullYear(), currentMonth?.getMonth(), day));
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
          Availability Calendar
        </h2>
        <p className="font-body text-sm text-muted-foreground">
          Select the dates when your trailer is available for rent
        </p>
      </div>
      {/* Pickup & Dropoff Time Settings */}
      <div className="p-5 bg-card border border-border rounded-lg space-y-4">
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={18} color="var(--color-primary)" strokeWidth={2} />
          <h3 className="font-heading text-base font-semibold text-foreground">
            Pickup &amp; Drop-off Times
          </h3>
        </div>
        <p className="font-body text-sm text-muted-foreground">
          Set the time window renters can pick up and drop off your trailer
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-caption text-xs font-medium text-muted-foreground uppercase mb-1.5">
              Earliest Pick-up Time
            </label>
            <div className="relative">
              <select
                value={pickupTime || ''}
                onChange={(e) => onPickupTimeChange?.(e?.target?.value)}
                className="w-full appearance-none bg-background border border-border rounded-md px-3 py-2.5 pr-8 font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-smooth"
              >
                <option value="">Select pick-up time</option>
                {TIME_OPTIONS?.map((time) => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
              <Icon name="ChevronDown" size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" strokeWidth={2} />
            </div>
          </div>
          <div>
            <label className="block font-caption text-xs font-medium text-muted-foreground uppercase mb-1.5">
              Latest Drop-off Time
            </label>
            <div className="relative">
              <select
                value={dropoffTime || ''}
                onChange={(e) => onDropoffTimeChange?.(e?.target?.value)}
                className="w-full appearance-none bg-background border border-border rounded-md px-3 py-2.5 pr-8 font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-smooth"
              >
                <option value="">Select drop-off time</option>
                {TIME_OPTIONS?.map((time) => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
              <Icon name="ChevronDown" size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" strokeWidth={2} />
            </div>
          </div>
        </div>
        {pickupTime && dropoffTime && (
          <div className="flex items-center space-x-2 text-sm text-success">
            <Icon name="CheckCircle" size={15} strokeWidth={2} />
            <span className="font-body">
              Renters can pick up from <strong>{pickupTime}</strong> and must return by <strong>{dropoffTime}</strong>
            </span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mb-4">
        <button onClick={handlePreviousMonth} className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-muted transition-smooth" aria-label="Previous month">
          <Icon name="ChevronLeft" size={20} strokeWidth={2} />
        </button>
        <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground">
          {monthNames?.[currentMonth?.getMonth()]} {currentMonth?.getFullYear()}
        </h3>
        <button onClick={handleNextMonth} className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-muted transition-smooth" aria-label="Next month">
          <Icon name="ChevronRight" size={20} strokeWidth={2} />
        </button>
      </div>
      <div className="flex items-center justify-end space-x-2 mb-4">
        <button onClick={handleSelectAll} className="px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-md transition-smooth">
          Select All
        </button>
        <button onClick={handleClearAll} className="px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted rounded-md transition-smooth">
          Clear All
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map((day) => (
          <div key={day} className="text-center font-caption text-xs md:text-sm font-medium text-muted-foreground py-2">{day}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 md:gap-2">
        {days?.map((date, index) => {
          if (!date) return <div key={index} className="aspect-square" />;
          const dateString = date?.toISOString()?.split('T')?.[0];
          const isSelected = selectedDates?.includes(dateString);
          const isPast = date < new Date(new Date()?.setHours(0, 0, 0, 0));
          return (
            <button
              key={index}
              onClick={() => !isPast && handleDateClick(date)}
              disabled={isPast}
              className={`aspect-square flex items-center justify-center rounded-md font-body text-sm md:text-base transition-smooth ${
                isPast ? 'text-muted-foreground/30 cursor-not-allowed'
                  : isSelected ? 'bg-primary text-primary-foreground font-semibold' :'text-foreground hover:bg-muted border border-border'
              }`}
            >
              {date?.getDate()}
            </button>
          );
        })}
      </div>
      <div className="flex items-center space-x-4 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-primary rounded" />
          <span className="font-body text-muted-foreground">Available</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 border border-border rounded" />
          <span className="font-body text-muted-foreground">Not Selected</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-muted-foreground/20 rounded" />
          <span className="font-body text-muted-foreground">Past Date</span>
        </div>
      </div>
      {selectedDates?.length > 0 && (
        <div className="p-4 bg-card border border-border rounded-md">
          <p className="font-body text-sm text-foreground">
            <span className="font-semibold">Selected Dates:</span> {selectedDates?.length} day{selectedDates?.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
      <div className="p-4 bg-muted rounded-md flex items-start space-x-3">
        <Icon name="Info" size={20} color="var(--color-primary)" strokeWidth={2} />
        <div className="flex-1">
          <p className="font-body text-sm text-foreground">
            <span className="font-semibold">Availability Tips:</span> Keep your calendar updated to avoid booking conflicts. You can always update availability later from your dashboard.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AvailabilitySection;